import axios, { AxiosError, AxiosInstance, AxiosPromise, AxiosResponse } from 'axios';
import utc from 'dayjs/plugin/utc';
import dayjs from 'dayjs';
import ModalStore, { ModalType } from 'src/stores/ModalStore';
import { GPSActivationResult } from 'src/types/commons/gps';
import { Invoice, InvoiceLine, InvoiceWithSparePart } from 'src/types/commons/invoices';
import { OrderNotification } from 'src/types/commons/notifications';
import { OrderHeader } from 'src/types/commons/orders';
import {
    Battery,
    Bike,
    BikeInService,
    CompatibleBike,
    ConfigurableParts,
    MarketingMaterialsResponse,
    SparePart,
    SparePartParentBike,
} from 'src/types/commons/products';
import { Reason, ReturnRequest, ReturnRequestType, SparePartsReturn } from 'src/types/commons/returns';
import { Translations } from 'src/types/commons/translation';
import { UserPreferences } from 'src/types/commons/users';
import CONFIG from 'src/utils/config';
import { salesForceLangCode } from 'src/utils/constants';
import { objectToQueryString } from 'src/utils/objectToQueryString';
import Security from '../Security';
import { CreateOrder } from './models';

const { API, APP_BRAND, IMPERSONATION_HEADER } = CONFIG;

type AuthHeader = {
    token: string;
};

class RestService {
    server: AxiosInstance;
    user: string = '';

    constructor() {
        dayjs.extend(utc);

        this.server = axios.create({
            baseURL: API,
            timeout: 40000,
        });

        if (Security.validUserCookie()) {
            const user = Security.getUser();
            if (user && user.token) {
                this.setHeaders({ token: user.token });
            }
        }

        this.server.interceptors.response.use(
            (response: AxiosResponse) => {
                const formattedResponse = { meta: response?.data?.meta, ...response };
                if (response.data && response.data.data) {
                    formattedResponse.data = response.data.data;
                }

                return formattedResponse;
            },
            (error: AxiosError) => {
                const originalRequest = error.config;
                const { status } = error.request;

                const redirectToLoginPage = () => {
                    Security.clean();
                    window.location.replace('/unauthorized');
                };

                if (status === 401) {
                    redirectToLoginPage();
                } else if (status === 503) {
                    // status 503 unavailable server, when Centraal lose connection to sf, retry if we still have user id
                    if (this.user && originalRequest) {
                        return axios.request(originalRequest);
                    } else {
                        redirectToLoginPage();
                    }
                } else if (
                    status !== 404 &&
                    !error.request.responseURL.match(/storage/) &&
                    !error.request.responseURL.match(/refresh-token/) &&
                    !error.request.responseURL.match(/res\/\.cloudinary/) &&
                    !error.request.responseURL.match(/notifications/) &&
                    !error.request.responseURL.match(/gps/)
                ) {
                    this.showErrorModal();
                }

                return Promise.reject(error);
            },
        );
    }

    showErrorModal() {
        ModalStore.set({
            title: 'notifications.error_title',
            message: 'notifications.error_message',
            type: ModalType.ERROR,
            buttonText: 'notifications.error_ok',
        });
    }

    setUser(user: string) {
        this.user = user;
    }

    // https://bitbucket.org/qwic/commons/src/master/src/types/Auth.ts
    setHeaders({ token }: AuthHeader) {
        this.server.defaults.headers.common.Authorization = `Bearer ${token}`;
    }

    setImpersonateHeaders(customerId: string) {
        if (customerId) {
            this.server.defaults.headers.common[IMPERSONATION_HEADER] = customerId;
        } else {
            delete this.server.defaults.headers.common[IMPERSONATION_HEADER];
        }
    }

    getTranslations(lang: string): Promise<Translations> {
        return axios
            .get(`https://dp-translations.s3.eu-central-1.amazonaws.com/${APP_BRAND}/${lang}.json`)
            .then((response: any) => response.data.data);
    }

    // User
    getUserProfile(): AxiosPromise {
        return this.server.get('/microPlatform/users/profile');
    }

    getStorageData(): AxiosPromise {
        return this.server.get('/microPlatform/users/storage');
    }

    updateStorageData(type: string, snapshot: string): AxiosPromise {
        return this.server.post('/microPlatform/users/storage', { type, snapshot });
    }

    getUserPreferences(): AxiosPromise {
        return this.server.get('/microPlatform/users/preferences');
    }

    changeUserPreferences(data: UserPreferences): AxiosPromise {
        return this.server.patch('/microPlatform/users/preferences', data);
    }

    createCaseContact(data: FormData): AxiosPromise {
        return this.server.post('/microPlatform/contact', data);
    }

    getRecentNews(lang: string, search: string = '', limit: number = 20, offset: number = 0): AxiosPromise {
        return this.server.get(`/microPlatform/wordpress/news?lang=${lang}&q=${encodeURIComponent(search)}&limit=${limit}&offset=${offset}`);
    }

    getMarketingBanners(lang: string, search: string): AxiosPromise {
        return this.server.get(`/microPlatform/wordpress/banners?lang=${lang}&q=${encodeURIComponent(search)}`);
    }

    // Bike assets
    getBikeAssets(limit: number, offset: number): AxiosPromise {
        return this.server.get(`/microPlatform/bikes?limit=${limit}&offset=${offset}`);
    }

    getOneBikeAssetById(SFId: string): AxiosPromise {
        return this.server.get(`/microPlatform/bikes/${SFId}`);
    }

    // Orders
    getOrderNotifications(dateFrom: dayjs.Dayjs, dateTo: dayjs.Dayjs = dayjs()): AxiosPromise<OrderNotification[]> {
        const dateFormat = 'YYYY-MM-DDTHH:mm:ss[Z]';
        return this.server.get(
            `/microPlatform/notification?dateFrom=${dateFrom.utc().format(dateFormat)}&dateTo=${dateTo.utc().format(dateFormat)}`,
        );
    }

    getOneOrderByOrderId(orderId: string | undefined): AxiosPromise<{ data: OrderHeader[] }> {
        return this.server.get(`/microPlatform/orders/${orderId}`);
    }

    getAllOrders(limit: number = 1000, offset: number = 0): AxiosPromise<{ data: OrderHeader[] }> {
        return this.server.get(`/microPlatform/orders?limit=${limit}&offset=${offset}`);
    }

    updateOrder(orderId: string, data: any): AxiosPromise {
        return this.server.patch(`/microPlatform/orders/${orderId}`, data);
    }

    updateOrderLine(orderId: string, data: { references: { [key: string]: string }[] }): AxiosPromise {
        return this.server.patch(`/microPlatform/orders/${orderId}/line`, data);
    }

    healthCheck(): AxiosPromise<{ data: { ok: boolean, message?: string } }> {
        return this.server.get('/microPlatform/orders/healthCheck');
    }

    placeOrder(createOrder: CreateOrder): AxiosPromise<{ redirectUrl?: string }> {
        return this.server.post('/microPlatform/orders', [createOrder]);
    }

    // Get price calculation for cart items from the BE
    getOrderQuote(
        createOrder: CreateOrder,
    ): AxiosPromise<{ totalPriceForDealer: number; totalPriceForConsumer: number }> {
        return this.server.post('/microPlatform/orders/quote', [createOrder]);
    }

    // Products
    getBikes(queryParams?: { serie: string; country: string }): AxiosPromise<Bike[]> {
        const params = objectToQueryString(queryParams);
        return this.server.get(`/microPlatform/products/bikes?${params}`);
    }

    getBikeSizes(): AxiosPromise<{ data: string[] }> {
        return this.server.get(`/microPlatform/products/bikes/sizes`);
    }

    getBikesInService(): AxiosPromise<{ data: BikeInService[] }> {
        return this.server.get(`/microPlatform/products/bikes/inService`);
    }

    getBikesAutocomplete(frameNumber: string): any {
        return this.server.get(
            `/microPlatform/products/bikes/autocomplete?frameNumber=${encodeURIComponent(frameNumber)}`,
        );
    }

    getBatteries(queryParams: { country: string }): AxiosPromise<Battery[]> {
        const params = objectToQueryString(queryParams);
        return this.server.get(`/microPlatform/products/batteries?${params}`);
    }

    getAllSpareParts(): AxiosPromise<{ data: SparePart[] }> {
        return this.server.get(`/microPlatform/products/spareParts`);
    }

    getAllSparePartsBySearchTerm(searchString: string, limit = 1000): AxiosPromise<{ data: SparePart[] }> {
        return this.server.get(`/microPlatform/products/spareParts?q=${encodeURIComponent(searchString)}&limit=${limit}`);
    }

    getSparePartsByPartId(partId: string): AxiosPromise<{ data: { spareParts: SparePart[] } }> {
        return this.server.get(`/microPlatform/products/spareParts/${partId}`);
    }

    getSparePartsByProductCode(
        productCode: string,
    ): AxiosPromise<{ data: { products: SparePart[]; parentBike: SparePartParentBike } }> {
        return this.server.get(`/microPlatform/products/spareParts?bikeProductCode=${productCode}`);
    }

    getSparePartsByFrameNumber(
        frameNumber: string,
    ): AxiosPromise<{ data: { products: SparePart[]; parentBike: SparePartParentBike } }> {
        return this.server.get(`/microPlatform/products/spareParts?frame=${frameNumber}`);
    }

    getCompatibleBikesByPartId(partId: string): AxiosPromise<{ data: CompatibleBike[] }> {
        return this.server.get(`/microPlatform/products/spareParts/${partId}/compatibleBikes`);
    }

    getConfigurablePartsByProductCode(productCode: string): AxiosPromise<{ data: ConfigurableParts[] }> {
        return this.server.get(`/microPlatform/products/configurable/${productCode}`);
    }

    getBikesByPartNumber(
        partNumber: string,
    ): AxiosPromise<{ data: { bom: string; code: string; description: string }[] }> {
        return this.server.get(`/microPlatform/products/bikes/partId/${partNumber}`);
    }

    getMarketingMaterials(limit: number = 1000, offset: number = 0): AxiosPromise<MarketingMaterialsResponse> {
        return this.server.get(`/microPlatform/products/marketingMaterial?limit=${limit}&offset=${offset}`);
    }

    getMarketingMaterialById(partId: string): AxiosPromise<MarketingMaterialsResponse> {
        return this.server.get(`/microPlatform/products/marketingMaterial/${partId}`);
    }

    // Invoices
    getInvoicesByAccountId(dateStart?: string, dateEnd?: string): AxiosPromise<Invoice[]> {
        let params = '';

        if (dateStart) {
            params += `?startDate=${encodeURIComponent(dateStart)}`;
        }

        if (dateEnd) {
            params += `&endDate=${encodeURIComponent(dateEnd)}`;
        }

        return this.server.get(`/microPlatform/invoices${params}`);
    }

    getInvoiceLinesByInvoiceId(id: string): AxiosPromise<{ data: InvoiceLine[] }> {
        return this.server.get(`/microPlatform/invoices/${id}/lines`);
    }

    getInvoicePdfById(id: string, lang: string): AxiosPromise {
        return this.server.get(`/microPlatform/invoices/${id}/file?lang=${lang}`, {
            responseType: 'blob',
        });
    }

    getInvoicesBySparePart(sparePartCode: string): AxiosPromise<{ data: InvoiceWithSparePart[] }> {
        return this.server.get(`/microPlatform/invoices/sparePart/${sparePartCode}`);
    }

    getInvoiceById(invoiceId: string): AxiosPromise<{ data: Invoice }> {
        return this.server.get(`/microPlatform/invoices/${invoiceId}`);
    }

    createPayment(invoices: string[]): AxiosPromise<{ url: string }> {
        return this.server.post('/dp/invoices/pay', { invoices });
    }

    // Return requests
    getReturnRequests(limit: number = 500, offset: number = 0): AxiosPromise<{ data: ReturnRequestType[] }> {
        return this.server.get(`/microPlatform/legacy/returnRequests?limit=${limit}&offset=${offset}`);
    }

    getReturnRequestById(returnId: string): AxiosPromise<{ data: ReturnRequestType }> {
        return this.server.get(`/microPlatform/legacy/returnRequests/${returnId}`);
    }

    getReturnRequestPdf(caseNumber: string, lang: string): AxiosPromise {
        return this.server.get(`/microPlatform/legacy/returnRequests/${caseNumber}/pdf?lang=${lang}`, {
            responseType: 'blob',
        });
    }

    createReturnRequest(data: ReturnRequest): AxiosPromise {
        return this.server.post('/dp/returnRequests', data);
    }

    getReturnReasons(): AxiosPromise<{ data: Reason[] }> {
        return this.server.get('/microPlatform/legacy/returnRequestReasons');
    }

    getSparePartsReturnStatus(productCode: string, category?: string): AxiosPromise<{ data: SparePartsReturn }> {
        const categoryParam = category ? `?category=${encodeURIComponent(category)}` : '';
        return this.server.get(`/microPlatform/legacy/sparePartsReturnStatus/${productCode}${categoryParam}`);
    }

    // GPS
    activateGPS(frameNumber: string, imei: string): AxiosPromise<GPSActivationResult> {
        return this.server.post('/microPlatform/conneqTech/activate', { frameNumber, imei });
    }

    // KnowledgeBase
    getKnowledgeBaseUrl(lang: string, returnUrl: string = '', useLegacy = false): AxiosPromise<string> {
        const mappedLang = lang ? salesForceLangCode[lang] : '';
        return this.server.get(`/microPlatform/${useLegacy ? 'legacy/' : ''}knowledgeBase?lang=${mappedLang}${returnUrl ? `&returnUrl=${returnUrl}` : ''}`);
    }

    // Upload
    uploadFile(data: FormData) {
        return this.server.post('/microPlatform/upload', data);
    }
}

// @TODO: make it into 1 instance, check how many instances are created;
export default new RestService();
